import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import Button from '../../components/ui/Button';
import EmojiPicker from '../../components/ui/EmojiPicker';
import '../../styles/AdminEntity.css';

// category.name is {en, hu, es} — pick current lang with EN fallback
const catName = (c, lang) => {
  const n = c?.name;
  if (!n || typeof n !== 'object') return String(n || '');
  return n[lang] || n.en || Object.values(n).find(Boolean) || '';
};

const Field = ({ form, onChange, label, name, type = 'text', ...rest }) => (
  <div className="ae-field">
    <label>{label}</label>
    <input name={name} type={type} value={form[name] ?? ''} onChange={onChange} {...rest} />
  </div>
);

const EMPTY_FORM = {
  company_name: '', contact_name: '', category_id: '',
  service_description: '', phone: '', email: '', website: '',
  whatsapp: '', facebook: '',
  address: '', billing_name: '', billing_tax_number: '', billing_address: '',
  languages: '', notes: '', is_active: true,
  area_ids: [], rating: null,
};

const StarRating = ({ value, onChange }) => (
  <div className="ae-field">
    <label>Rating</label>
    <div className="ae-stars">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          className={`ae-star ${n <= (value || 0) ? 'filled' : ''}`}
          onClick={() => onChange(n === value ? null : n)}
          title={`${n} star${n > 1 ? 's' : ''}`}
        >★</button>
      ))}
      {value && (
        <button type="button" className="ae-star-clear" onClick={() => onChange(null)} title="Clear rating">✕</button>
      )}
    </div>
  </div>
);

const EMPTY_CAT = { name_en: '', name_hu: '', name_es: '', icon: '', sort_order: 0 };

const PartnersPage = () => {
  const { api } = useAuth();
  const { language } = useI18n();

  const [partners, setPartners]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [areas, setAreas]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [msg, setMsg]               = useState({ text: '', type: '' });

  const [search, setSearch]           = useState('');
  const [filterCat, setFilterCat]     = useState('');
  const [filterActive, setFilterActive] = useState('');

  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);

  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting]       = useState(false);
  const importRef                       = React.useRef();

  const handleExport = async (template) => {
    try {
      const r = await api.get(`/partners/export${template ? '?template=1' : ''}`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = template ? 'partners_template.xlsx' : 'partners_export.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) { flash('Export failed', 'error'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setImportResult(null);
    try {
      const buffer = await file.arrayBuffer();
      const r = await api.post('/partners/import', buffer, { headers: { 'Content-Type': 'application/octet-stream' } });
      setImportResult(r.data);
      if (r.data.created > 0) load();
    } catch (err) {
      flash(err.response?.data?.error || err.message || 'Import failed', 'error');
    } finally { setImporting(false); }
  };

  const [catForm, setCatForm]         = useState(EMPTY_CAT);
  const [catEditing, setCatEditing]   = useState(null);
  const [catSaving, setCatSaving]     = useState(false);
  const [catTranslating, setCatTranslating] = useState(false);
  const [showCats, setShowCats]       = useState(false);

  const flash = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes, aRes] = await Promise.all([
        api.get('/partners'),
        api.get('/partners/categories'),
        api.get('/partners/areas'),
      ]);
      setPartners(pRes.data);
      setCategories(cRes.data);
      setAreas(aRes.data);
    } catch { flash('Failed to load data', 'error'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY_FORM); setEditing('new'); };

  const openEdit = async (id) => {
    try {
      const r = await api.get(`/partners/${id}`);
      const p = r.data;
      setForm({
        company_name: p.company_name ?? '',
        contact_name: p.contact_name ?? '',
        category_id: p.category_id ?? '',
        service_description: p.service_description ?? '',
        phone: p.phone ?? '',
        email: p.email ?? '',
        website: p.website ?? '',
        whatsapp: p.whatsapp ?? '',
        facebook: p.facebook ?? '',
        address: p.address ?? '',
        billing_name: p.billing_name ?? '',
        billing_tax_number: p.billing_tax_number ?? '',
        billing_address: p.billing_address ?? '',
        languages: p.languages ?? '',
        notes: p.notes ?? '',
        is_active: p.is_active !== 0,
        area_ids: (p.service_areas || []).map(a => a.id),
        rating: p.rating ?? null,
      });
      setEditing(id);
    } catch { flash('Failed to load partner', 'error'); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleArea = (id) => {
    setForm(f => {
      const ids = f.area_ids.includes(id)
        ? f.area_ids.filter(x => x !== id)
        : [...f.area_ids, id];
      return { ...f, area_ids: ids };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/partners', form);
        flash('Partner created');
      } else {
        await api.put(`/partners/${editing}`, form);
        flash('Partner updated');
      }
      setEditing(null);
      load();
    } catch (err) {
      flash(err.response?.data?.error || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/partners/${id}`);
      setPartners(p => p.filter(x => x.id !== id));
      flash('Deleted');
    } catch (err) { flash(err.response?.data?.error || 'Delete failed', 'error'); }
  };

  const handleCatNameBlur = async (sourceLang, value) => {
    if (!value?.trim()) return;
    const others = ['en', 'hu', 'es'].filter(l => l !== sourceLang);
    const allFilled = others.every(l => catForm[`name_${l}`]?.trim());
    if (allFilled) return;
    setCatTranslating(true);
    try {
      const r = await api.post('/partners/categories/translate', { text: value.trim(), source: sourceLang });
      setCatForm(f => {
        const next = { ...f };
        for (const lang of others) {
          if (!f[`name_${lang}`]?.trim() && r.data[lang]) {
            next[`name_${lang}`] = r.data[lang];
          }
        }
        return next;
      });
    } catch { /* fordítás opcionális */ }
    finally { setCatTranslating(false); }
  };

  // Category CRUD
  const saveCat = async (e) => {
    e.preventDefault();
    setCatSaving(true);
    try {
      if (catEditing === 'new') {
        await api.post('/partners/categories', catForm);
      } else {
        await api.put(`/partners/categories/${catEditing}`, catForm);
      }
      setCatEditing(null);
      setCatForm(EMPTY_CAT);
      const r = await api.get('/partners/categories');
      setCategories(r.data);
      flash('Category saved');
    } catch (err) { flash(err.response?.data?.error || 'Failed', 'error'); }
    finally { setCatSaving(false); }
  };

  const deleteCat = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await api.delete(`/partners/categories/${id}`);
      setCategories(c => c.filter(x => x.id !== id));
    } catch (err) { flash(err.response?.data?.error || 'Delete failed', 'error'); }
  };

  const filtered = partners.filter(p => {
    if (filterCat && String(p.category_id) !== filterCat) return false;
    if (filterActive === 'true'  && !p.is_active) return false;
    if (filterActive === 'false' &&  p.is_active) return false;
    return [p.company_name, p.contact_name, p.email, p.service_areas]
      .join(' ').toLowerCase().includes(search.toLowerCase());
  });

  if (editing !== null) {
    return (
      <div className="ae-page">
        {msg.text && <div className={`ae-msg ${msg.type}`}>{msg.text}</div>}
        <div className="ae-card">
          <div className="ae-form-header">
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>← Back</Button>
            <h2 className="ae-form-title">{editing === 'new' ? 'New Partner' : 'Edit Partner'}</h2>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.65rem' }}>
              <Button variant="ghost" type="button" onClick={() => setEditing(null)}>Cancel</Button>
              <Button variant="solid" type="submit" form="partner-form" disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
          <form id="partner-form" onSubmit={handleSave}>
            <div className="ae-form-body">

              <div className="ae-form-section">
                <p className="ae-form-section-title">Company</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label="Company name *" name="company_name" required />
                  <Field form={form} onChange={handleChange} label="Contact name" name="contact_name" />
                  <div className="ae-field">
                    <label>Category</label>
                    <select name="category_id" value={form.category_id} onChange={handleChange}>
                      <option value="">— none —</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {catName(c, language)}</option>)}
                    </select>
                  </div>
                  <div className="ae-checkbox-row">
                    <input type="checkbox" id="chk-active" name="is_active" checked={!!form.is_active} onChange={handleChange} />
                    <label htmlFor="chk-active">Active</label>
                  </div>
                  <StarRating value={form.rating} onChange={v => setForm(f => ({ ...f, rating: v }))} />
                </div>
                <div className="ae-field" style={{ marginTop: '0.75rem' }}>
                  <label>Service description</label>
                  <textarea name="service_description" value={form.service_description} onChange={handleChange} />
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">Contact</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label="Phone" name="phone" />
                  <Field form={form} onChange={handleChange} label="Email" name="email" type="email" />
                  <Field form={form} onChange={handleChange} label="Website" name="website" />
                  <Field form={form} onChange={handleChange} label="WhatsApp" name="whatsapp" />
                  <Field form={form} onChange={handleChange} label="Facebook" name="facebook" />
                  <Field form={form} onChange={handleChange} label="Languages" name="languages" placeholder="EN, ES, HU…" />
                </div>
                <div className="ae-field" style={{ marginTop: '0.75rem' }}>
                  <label>Address</label>
                  <input name="address" type="text" value={form.address} onChange={handleChange} />
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">Billing</p>
                <div className="ae-grid">
                  <Field form={form} onChange={handleChange} label="Billing name" name="billing_name" />
                  <Field form={form} onChange={handleChange} label="Tax number" name="billing_tax_number" />
                  <Field form={form} onChange={handleChange} label="Billing address" name="billing_address" />
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">Service areas</p>
                <div className="ae-areas-grid">
                  {areas.map(a => (
                    <label key={a.id} className="ae-area-check">
                      <input
                        type="checkbox"
                        checked={form.area_ids.includes(a.id)}
                        onChange={() => toggleArea(a.id)}
                      />
                      {a.name}
                    </label>
                  ))}
                </div>
              </div>

              <div className="ae-form-section">
                <p className="ae-form-section-title">Notes</p>
                <div className="ae-field">
                  <label>Notes</label>
                  <textarea name="notes" value={form.notes} onChange={handleChange} />
                </div>
              </div>

            </div>
            <div className="ae-form-footer">
              <Button variant="ghost" type="button" onClick={() => setEditing(null)}>Cancel</Button>
              <Button variant="solid" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="ae-page">
      {msg.text && <div className={`ae-msg ${msg.type}`}>{msg.text}</div>}

      {importResult && (
        <div className={`ae-msg ${importResult.errors?.length ? 'error' : importResult.warnings?.length ? 'warning' : 'success'}`} style={{ position: 'relative' }}>
          <button onClick={() => setImportResult(null)} style={{ position: 'absolute', right: '0.75rem', top: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem' }}>✕</button>
          <strong>Import complete:</strong> {importResult.created} created, {importResult.updated || 0} updated, {importResult.skipped} skipped.
          {importResult.warnings?.length > 0 && (
            <>
              <div style={{ marginTop: '0.4rem', fontWeight: 600, fontSize: '0.8rem' }}>⚠ Warnings:</div>
              <ul style={{ margin: '0.2rem 0 0', paddingLeft: '1.2rem', fontSize: '0.8rem' }}>
                {importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </>
          )}
          {importResult.errors?.length > 0 && (
            <>
              <div style={{ marginTop: '0.4rem', fontWeight: 600, fontSize: '0.8rem' }}>✕ Errors:</div>
              <ul style={{ margin: '0.2rem 0 0', paddingLeft: '1.2rem', fontSize: '0.8rem' }}>
                {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="ae-card ae-header-card">
        <div className="ae-topbar">
          <h1 className="ae-title">Partners</h1>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Button variant="ghost" size="sm" onClick={() => setShowCats(s => !s)}>
              {showCats ? 'Hide categories' : 'Manage categories'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleExport(false)} title="Export partners as CSV">↓ Export</Button>
            <Button variant="ghost" size="sm" onClick={() => handleExport(true)} title="Download empty CSV template">↓ Template</Button>
            <Button variant="ghost" size="sm" onClick={() => importRef.current?.click()} disabled={importing}>
              {importing ? 'Importing…' : '↑ Import'}
            </Button>
            <input ref={importRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: 'none' }} onChange={handleImport} />
            <Button variant="solid" onClick={openNew}>+ Add partner</Button>
          </div>
        </div>
        <div className="ae-filters">
          <input className="ae-search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
          <select className="ae-select" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
            <option value="">All categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {catName(c, language)}</option>)}
          </select>
          <select className="ae-select" value={filterActive} onChange={e => setFilterActive(e.target.value)}>
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* Category management */}
      {showCats && (
        <div className="ae-card" style={{ marginBottom: '1.25rem' }}>
          <div className="ae-form-header">
            <h2 className="ae-form-title">Categories</h2>
            {catEditing === null && (
              <Button variant="ghost" size="sm" onClick={() => { setCatForm(EMPTY_CAT); setCatEditing('new'); }}>
                + Add
              </Button>
            )}
          </div>
          {catEditing !== null && (
            <form onSubmit={saveCat} style={{ padding: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', borderBottom: '1px solid var(--border-color)' }}>
              <div className="ae-field" style={{ minWidth: 160 }}>
                <label>Name EN *{catTranslating && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--text-muted)' }}>translating…</span>}</label>
                <input required value={catForm.name_en}
                  onChange={e => setCatForm(f => ({ ...f, name_en: e.target.value }))}
                  onBlur={e => handleCatNameBlur('en', e.target.value)}
                  placeholder="e.g. Plumbing" />
              </div>
              <div className="ae-field" style={{ minWidth: 160 }}>
                <label>Name HU{catTranslating && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--text-muted)' }}>translating…</span>}</label>
                <input value={catForm.name_hu}
                  onChange={e => setCatForm(f => ({ ...f, name_hu: e.target.value }))}
                  onBlur={e => handleCatNameBlur('hu', e.target.value)}
                  placeholder="pl. Vízszerelés" />
              </div>
              <div className="ae-field" style={{ minWidth: 160 }}>
                <label>Name ES{catTranslating && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--text-muted)' }}>translating…</span>}</label>
                <input value={catForm.name_es}
                  onChange={e => setCatForm(f => ({ ...f, name_es: e.target.value }))}
                  onBlur={e => handleCatNameBlur('es', e.target.value)}
                  placeholder="ej. Fontanería" />
              </div>
              <div className="ae-field" style={{ minWidth: 100 }}>
                <label>Icon</label>
                <EmojiPicker value={catForm.icon} onChange={icon => setCatForm(f => ({ ...f, icon }))} />
              </div>
              <div className="ae-field" style={{ minWidth: 80 }}>
                <label>Order</label>
                <input type="number" value={catForm.sort_order} onChange={e => setCatForm(f => ({ ...f, sort_order: Number(e.target.value) }))} />
              </div>
              <Button variant="solid" size="sm" type="submit" disabled={catSaving}>Save</Button>
              <Button variant="ghost" size="sm" type="button" onClick={() => setCatEditing(null)}>Cancel</Button>
            </form>
          )}
          <div className="ae-table-wrap">
            <table className="ae-table">
              <thead><tr><th>Icon</th><th>EN</th><th>HU</th><th>ES</th><th>Order</th><th></th></tr></thead>
              <tbody>
                {categories.map(c => (
                  <tr key={c.id}>
                    <td>{c.icon}</td>
                    <td className="ae-name">{c.name?.en || '—'}</td>
                    <td>{c.name?.hu || '—'}</td>
                    <td>{c.name?.es || '—'}</td>
                    <td>{c.sort_order}</td>
                    <td>
                      <div className="ae-actions">
                        <Button variant="ghost" size="sm" onClick={() => { setCatForm({ name_en: c.name?.en || '', name_hu: c.name?.hu || '', name_es: c.name?.es || '', icon: c.icon || '', sort_order: c.sort_order }); setCatEditing(c.id); }}>Edit</Button>
                        <Button variant="danger" size="sm" onClick={() => deleteCat(c.id, catName(c, 'en'))}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {categories.length === 0 && <tr><td colSpan={4} className="ae-empty">No categories</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="ae-card">
        {loading ? (
          <div className="ae-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="ae-empty">No partners found</div>
        ) : (
          <div className="ae-table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Category</th>
                  <th>Rating</th>
                  <th>Phone</th>
                  <th>Areas</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td className="ae-name">{p.company_name}</td>
                    <td>{catName({ name: p.category_name }, language) || '—'}</td>
                    <td>
                      <span className="ae-stars-display">
                        {p.rating
                          ? [1,2,3,4,5].map(n => (
                              <span key={n} className={n <= p.rating ? 'star-on' : 'star-off'}>★</span>
                            ))
                          : <span className="star-none">—</span>
                        }
                      </span>
                    </td>
                    <td>{p.phone || '—'}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.service_areas || '—'}
                    </td>
                    <td>
                      <span className={`ae-badge ${p.is_active ? 'active' : 'inactive'}`}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="ae-actions">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p.id)}>Edit</Button>
                        <Button variant="danger" size="sm" onClick={() => handleDelete(p.id, p.company_name)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnersPage;
